import { useState } from "react";
import BackgroundImage from "../images/research-background.jpg";
import AgingIcon from "../images/aging-icon.png";
import CommunityIcon from "../images/community-icon.png";
import TechnologyIcon from "../images/technology-icon.png";
import WorkforceIcon from "../images/workforce-icon.png";
import ResearchInquiryModal from "./ResearchInquiryModal";
import "./ResearchPage.css";

const StepIconListen = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M4 12a8 8 0 0 1 16 0v4a3 3 0 0 1-3 3h-1v-8h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 12v4a3 3 0 0 0 3 3h1v-8H4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const StepIconCoDesign = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="7" cy="8" r="3" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="17" cy="8" r="3" stroke="currentColor" strokeWidth="1.8" />
    <path d="M2 20c0-2.8 2.2-5 5-5s5 2.2 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M12 20c0-2.8 2.2-5 5-5s5 2.2 5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);
const StepIconExplore = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path d="M15.5 8.5l-2 5-5 2 2-5 5-2z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);
const StepIconLearn = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 3l9 4-9 4-9-4 9-4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M7 9v5c0 1.7 2.2 3 5 3s5-1.3 5-3V9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 7v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const PROCESS_STEPS = [
  {
    Icon: StepIconListen,
    title: "Listen",
    desc: "Real-world community needs",
  },
  {
    Icon: StepIconCoDesign,
    title: "Co-design",
    desc: "Collaborative solution development",
  },
  {
    Icon: StepIconExplore,
    title: "Explore",
    desc: "Responsible evaluation",
  },
  {
    Icon: StepIconLearn,
    title: "Learn",
    desc: "Learning and improvement",
  },
];

const INTEREST_AREAS = [
  {
    icon: AgingIcon,
    title: "Aging at Home",
    desc: "Exploring services and supports that promote safety, independence and community connection.",
  },
  {
    icon: CommunityIcon,
    title: "Community-Based Support",
    desc: "Understanding how flexible and person-centred services can respond to the changing needs of older adults and their families.",
  },
  {
    icon: TechnologyIcon,
    title: "Digital Health and Technology",
    desc: "Exploring responsible uses of digital platforms, intelligent systems and emerging technologies to support aging at home.",
  },
  {
    icon: WorkforceIcon,
    title: "Service and Workforce Innovation",
    desc: "Learning how service coordination, provider experiences and sustainable workforce models can be improved.",
  },
];

const ResearchPage = () => {
  const [isInquiryOpen, setInquiryOpen] = useState(false);

  return (
    <div className="RI-container">
      <div
        className="RI-hero"
        style={{ backgroundImage: `url(${BackgroundImage})` }}
      >
        <div className="RI-hero-content">
          <h1 className="RI-title">Research and Innovation</h1>
          <p className="RI-hero-subtitle">
            Community-informed approaches to aging at home
          </p>
        </div>
      </div>

      <div className="RI-main-content">
        {/* Intro */}
        <section className="RI-section RI-intro">
          <h2 className="RI-section-title">
            Exploring community-informed approaches to aging at home
          </h2>
          <div className="RI-prose">
            <p>
              iCare Village is interested in supporting research and innovation
              that can improve how older adults remain safe, connected and
              independent in their homes and communities.
            </p>
            <p>
              By bringing together older adults, caregivers, service providers,
              researchers, students, community organizations and technology
              partners, we hope to create opportunities to better understand
              real-world challenges and explore practical, person-centred
              solutions.
            </p>
          </div>
        </section>

        {/* Living-Lab Vision */}
        <section className="RI-section">
          <h2 className="RI-section-title">Our Living-Lab Vision</h2>
          <div className="RI-prose">
            <p>
              Our long-term vision is to develop iCare Village as a
              community-based living lab for aging at home. This would provide
              an environment where community needs can inform research
              questions, potential solutions can be co-designed, and emerging
              approaches can be explored in real-world contexts.
            </p>
            <p>
              Research activities would be developed through appropriate
              partnerships and would proceed only after applicable ethical,
              privacy, institutional and organizational requirements have been
              addressed.
            </p>
          </div>

          {/* Connected process — numbered stepper with progress rail */}
          <div
            className="RI-process"
            role="list"
            aria-label="Our iterative process: Listen, Co-design, Explore, Learn"
          >
            <div className="RI-process-rail" aria-hidden="true" />
            <div className="RI-process-loop" aria-hidden="true">
              <svg viewBox="0 0 120 40" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M110 6 C 118 20, 60 38, 10 34"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeDasharray="4 4"
                  strokeLinecap="round"
                />
                <polyline
                  points="14,28 8,34 14,38"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="RI-process-loop-label">iterates</span>
            </div>

            {PROCESS_STEPS.map((step, i) => {
              const { Icon } = step;
              return (
                <div className="RI-step" role="listitem" key={step.title}>
                  <div className="RI-step-node">
                    <span className="RI-step-num">{String(i + 1).padStart(2, "0")}</span>
                    <span className="RI-step-icon"><Icon /></span>
                  </div>
                  <div className="RI-step-card">
                    <h3 className="RI-step-heading">{step.title}</h3>
                    <p className="RI-step-copy">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="RI-process-caption">
            An iterative process — each stage informs the next.
          </p>
        </section>

        {/* Areas of Research and Innovation */}
        <section className="RI-section">
          <h2 className="RI-section-title">Areas of Research and Innovation</h2>
          <p className="RI-section-lede">
            Four connected themes shape our research priorities — each grounded in
            real-world needs from older adults, families and care partners.
          </p>
          <div className="RI-card-grid">
            {INTEREST_AREAS.map((area, i) => (
              <article key={area.title} className="RI-card" tabIndex={0}>
                <span className="RI-card-accent" aria-hidden="true" />
                <div className="RI-card-head">
                  <span className="RI-card-eyebrow">
                    Area {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="RI-card-icon-wrap">
                    <img className="RI-card-icon" src={area.icon} alt="" />
                  </div>
                </div>
                <h3 className="RI-card-title">{area.title}</h3>
                <p className="RI-card-desc">{area.desc}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Working Together — full width */}
        <section className="RI-section RI-working">
          <h2 className="RI-section-title">Working Together</h2>
          <div className="RI-prose">
            <p>
              In the future, research and innovation opportunities may involve
              older adults, caregivers, service providers, students,
              researchers, community organizations and technology partners.
            </p>
            <p>
              These opportunities could include community consultations,
              student projects, co-design activities, technology evaluation and
              formally approved research studies.
            </p>
          </div>
        </section>

        {/* Contact CTA — separate */}
        <section className="RI-section RI-cta">
          <div className="RI-cta-inner">
            <h2 className="RI-section-title RI-cta-title">
              Interested in Research or Innovation?
            </h2>
            <p className="RI-cta-desc">
              We welcome preliminary conversations with researchers, students,
              community organizations and technology partners interested in
              research and innovation related to aging at home.
            </p>
            <button
              className="RI-discuss-button"
              onClick={() => setInquiryOpen(true)}
              type="button"
            >
              Discuss a Research or Innovation Idea
            </button>
          </div>
        </section>

        {/* Clarification */}
        <p className="RI-disclaimer">
          Information on this page describes iCare Village&rsquo;s future
          research and innovation vision. It does not represent an active
          research study, an invitation to participate in research or an
          institutional research partnership.
        </p>
      </div>

      <ResearchInquiryModal
        open={isInquiryOpen}
        onClose={() => setInquiryOpen(false)}
      />
    </div>
  );
};

export default ResearchPage;
